
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  Box,
  Divider,
  Grid,
  Typography,
} from "@mui/material";

import { Button } from "../../controls";
import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "./ReportPrintButton";

const ViewRegister = ({ setShowForm, viewData }) => {
  const { t } = useTranslation();
  const reportRef = useRef(null);

  const store = viewData?.store;

  if (!viewData) return null;

  const currency = (value) =>
    formatCurrency(value, store);

  const DetailRow = ({ label, value }) => (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          py: 0.75,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            flex: "1 1 55%",
          }}
        >
          {label}:
        </Typography>

        <Typography
          variant="body2"
          sx={{
            flex: "1 1 45%",
            textAlign: "right",
            overflowWrap: "anywhere",
          }}
        >
          {value ?? ""}
        </Typography>
      </Box>

      <Divider />
    </>
  );

  const Section = ({ title, children }) => (
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: 1,
        p: 2,
        height: "100%",
        boxSizing: "border-box",
        backgroundColor: "#fff",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: 17,
          fontWeight: 600,
          mb: 1.5,
          borderLeft: "4px solid #444",
          pl: 1,
        }}
      >
        {title}
      </Typography>

      {children}
    </Box>
  );

  // Shared screen and print report
  const RegisterContent = () => (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
          }}
        >
          {t("registerDetails")}
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ textAlign: "center" }}
        >
          {store?.storeName || ""}
        </Typography>

        <Typography
          variant="body2"
          sx={{ textAlign: "center" }}
        >
          {viewData.date || ""}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Cashier Details */}
        <Grid item xs={12} md={6}>
          <Section title={t("cashierDetails")}>
            <DetailRow
              label={t("storeName")}
              value={store?.storeName}
            />

            <DetailRow
              label={t("address")}
              value={store?.address}
            />

            <DetailRow
              label={t("cashierName")}
              value={[
                viewData.user?.firstName,
                viewData.user?.lastName,
              ]
                .filter(Boolean)
                .join(" ")}
            />

            <DetailRow
              label={t("saleDate")}
              value={viewData.date}
            />

            <DetailRow
              label={t("timeIn")}
              value={viewData.openTime}
            />

            <DetailRow
              label={t("timeOut")}
              value={viewData.closeTime}
            />
          </Section>
        </Grid>

        {/* Register Details */}
        <Grid item xs={12} md={6}>
          <Section title={t("registerDetails")}>
            <DetailRow
              label={t("totalCustomers")}
              value={viewData.totalCstmr}
            />

            <DetailRow
              label={t("averagePerCustomer")}
              value={currency(
                viewData.averagePerCstmr
              )}
            />

            <DetailRow
              label={t("totalSaleRefund")}
              value={currency(
                viewData.totalRefund
              )}
            />

            <DetailRow
              label={t("totalVoidSells")}
              value={currency(
                viewData.totalVoidSale
              )}
            />

            <DetailRow
              label={t("totalItemCanceled")}
              value={viewData.itemCancel}
            />
          </Section>
        </Grid>

        {/* Sale Report */}
        <Grid item xs={12} md={6}>
          <Section title={t("saleReport")}>
            <DetailRow
              label={t("totalSale")}
              value={currency(
                viewData.totalSale
              )}
            />

            <DetailRow
              label={t("totalCashSale")}
              value={currency(
                viewData.totalCash
              )}
            />

            <DetailRow
              label={t("totalCreditSale")}
              value={currency(
                viewData.totalCredit
              )}
            />

            <DetailRow
              label={t("otherPaymentReceived")}
              value={currency(
                viewData.otherPayment
              )}
            />
          </Section>
        </Grid>

        {/* Sell Details */}
        <Grid item xs={12} md={6}>
          <Section title={t("sellDetails")}>
            <DetailRow
              label={t("totalGrossSale")}
              value={currency(
                viewData.totalSale
              )}
            />

            <DetailRow
              label={t("totalTaxItemSold")}
              value={viewData.taxItemSold}
            />

            <DetailRow
              label={t("totalNonTaxItemSold")}
              value={viewData.nonTaxItemSold}
            />

            <DetailRow
              label={t("totalTaxReceived")}
              value={currency(
                viewData.taxReceived
              )}
            />
          </Section>
        </Grid>

        {/* Money */}
        <Grid item xs={12} md={6}>
          <Section title={t("money")}>
            <DetailRow
              label={t("totalCashSale")}
              value={currency(
                viewData.totalCash
              )}
            />

            <DetailRow
              label={t("shiftTransactionsBalance")}
              value={currency(
                viewData.shiftTransBalanace
              )}
            />
          </Section>
        </Grid>

        {/* Notes */}
        <Grid item xs={12} md={6}>
          <Section title={t("notes")}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {viewData.notes || ""}
            </Typography>
          </Section>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Box>
      {/* Screen Print Preview button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("registerDetails")}
        />
      </Box>

      {/* Normal screen details */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <RegisterContent />
      </Box>

      {/* Separate printable details */}
      <Box
        ref={reportRef}
        className="register-details-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <RegisterContent />
      </Box>

      {/* Close button - screen only */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 3,
          mb: 2,
        }}
      >
        <Button
          onClick={() => setShowForm(false)}
          className="btn-error"
          size="large"
          sx={{ minWidth: "150px" }}
        >
          {t("close")}
        </Button>
      </Box>
    </Box>
  );
};

export default ViewRegister;
